import { tracked } from "@glimmer/tracking";
import EmberObject, { computed } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import ColorSchemeColor from "discourse/admin/models/color-scheme-color";
import { ajax } from "discourse/lib/ajax";
import LegacyArrayLikeObject from "discourse/lib/legacy-array-like-object";
import { autoTrackedArray } from "discourse/lib/tracked-tools";
import { i18n } from "discourse-i18n";

class ColorSchemes extends LegacyArrayLikeObject {
  @tracked loading = false;
}

export default class ColorScheme extends EmberObject {
  static async findAll() {
    const colorSchemes = ColorSchemes.create({ content: [], loading: true });
    const all = await ajax("/admin/color_schemes");

    all.forEach((colorScheme) => {
      colorSchemes.push(
        ColorScheme.create({
          id: colorScheme.id,
          name: colorScheme.name,
          is_base: colorScheme.is_base,
          theme_id: colorScheme.theme_id,
          theme_name: colorScheme.theme_name,
          base_scheme_id: colorScheme.base_scheme_id,
          user_selectable: colorScheme.user_selectable,
          colors: colorScheme.colors,
        })
      );
    });

    return colorSchemes;
  }

  static async find(id) {
    const json = await ajax(`/admin/config/colors/${id}`);
    return ColorScheme.create({
      id: json.id,
      name: json.name,
      is_base: json.is_base,
      theme_id: json.theme_id,
      theme_name: json.theme_name,
      base_scheme_id: json.base_scheme_id,
      user_selectable: json.user_selectable,
      colors: json.colors,
    });
  }

  @tracked name;
  @tracked user_selectable;
  @tracked id;
  @autoTrackedArray colors;

  init() {
    super.init(...arguments);

    /** @type Array */
    const colors = this.colors ?? [];
    this.colors = colors.map((c) => {
      return ColorSchemeColor.create(c);
    });
    this.startTrackingChanges();
  }

  @dependentKeyCompat
  get newRecord() {
    return !this.id;
  }

  @computed
  get description() {
    return "" + this.name;
  }

  @computed("name", "user_selectable", "colors.@each.changed", "saving")
  get changed() {
    if (!this.originals) {
      return false;
    }
    if (this.originals.name !== this.name) {
      return true;
    }
    if (this.originals.user_selectable !== this.user_selectable) {
      return true;
    }
    if (this.colors.some((c) => c.get("changed"))) {
      return true;
    }

    return false;
  }

  @computed("changed")
  get disableSave() {
    if (this.theme_id) {
      return false;
    }

    return (
      !this.changed || this.saving || this.colors.some((c) => !c.get("valid"))
    );
  }

  startTrackingChanges() {
    this.set("originals", {
      name: this.name,
      user_selectable: this.user_selectable,
    });
  }

  schemeJson() {
    const buffer = [];
    this.colors.forEach((c) => {
      buffer.push(`  "${c.get("name")}": "${c.get("hex")}"`);
    });

    return [`"${this.name}": {`, buffer.join(",\n"), "}"].join("\n");
  }

  schemeObject() {
    return {
      name: this.name,
      light: Object.fromEntries(
        this.colors.map((color) => [color.name, color.hex])
      ),
    };
  }

  /**
   * @returns a JSON representation of the color scheme.
   */
  dump() {
    return JSON.stringify(this.schemeObject(), null, 2);
  }

  copy() {
    const newScheme = ColorScheme.create({
      name: this.name,
      can_edit: true,
      /** @type Array */
      colors: [],
    });
    this.colors.forEach((c) => {
      newScheme.colors.push(
        ColorSchemeColor.create(c.getProperties("name", "hex", "default_hex"))
      );
    });
    return newScheme;
  }

  save(opts) {
    if (!opts?.forceSave && (this.is_base || this.disableSave)) {
      return;
    }

    this.setProperties({ savingStatus: i18n("saving"), saving: true });

    const data = {};
    if (!opts || !opts.enabledOnly) {
      data.name = this.name;

      if (!opts?.saveNameOnly) {
        data.user_selectable = this.user_selectable;
        data.default_light_on_theme = this.default_light_on_theme;
        data.default_dark_on_theme = this.default_dark_on_theme;
        data.base_scheme_id = this.base_scheme_id;
        data.colors = [];
        this.colors.forEach((c) => {
          if (!this.id || c.get("changed")) {
            data.colors.push(c.getProperties("name", "hex"));
          }
        });
      }
    }

    return ajax(`/admin/color_schemes${this.id ? `/${this.id}` : ""}.json`, {
      data: JSON.stringify({ color_scheme: data }),
      type: this.id ? "PUT" : "POST",
      dataType: "json",
      contentType: "application/json",
    }).then((result) => {
      if (result.id) {
        this.set("id", result.id);
      }
      this.set("base_scheme_id", result.base_scheme_id);

      if (!opts?.saveNameOnly) {
        if (!opts || !opts.enabledOnly) {
          this.startTrackingChanges();
          this.colors.forEach((c) => c.startTrackingChanges());
        }
      }

      this.setProperties({ savingStatus: i18n("saved"), saving: false });
      this.notifyPropertyChange("description");
    });
  }

  updateUserSelectable(value) {
    if (!this.id) {
      return;
    }

    return ajax(`/admin/color_schemes/${this.id}.json`, {
      data: JSON.stringify({ color_scheme: { user_selectable: value } }),
      type: "PUT",
      dataType: "json",
      contentType: "application/json",
    });
  }

  updateDefaultOnTheme(field, value) {
    if (!this.id) {
      return;
    }

    return ajax(`/admin/color_schemes/${this.id}.json`, {
      data: JSON.stringify({ color_scheme: { [field]: value } }),
      type: "PUT",
      dataType: "json",
      contentType: "application/json",
    });
  }

  /** @returns {any} */
  destroy() {
    if (this.id) {
      return ajax(`/admin/color_schemes/${this.id}`, { type: "DELETE" });
    }
  }
}
